/**
 * Calculate the approximate size of data in bytes
 */
export function calculateDataSize(data: unknown): number {
  if (data === null || data === undefined) {
    return 0;
  }

  // For arrays, calculate size of all elements
  if (Array.isArray(data)) {
    return data.reduce((total, item) => total + calculateDataSize(item), 0);
  }

  // For objects, calculate size of all properties
  if (typeof data === 'object') {
    return Object.values(data).reduce((total, value) => total + calculateDataSize(value), 0);
  }

  // For primitives, estimate size
  if (typeof data === 'string') {
    return new Blob([data]).size;
  }

  if (typeof data === 'number') {
    return 8; // 8 bytes for a number
  }

  if (typeof data === 'boolean') {
    return 1; // 1 byte for a boolean
  }

  // Fallback: stringify and measure
  try {
    return new Blob([JSON.stringify(data)]).size;
  } catch {
    return 0;
  }
}

/**
 * Get the number of rows in data (for arrays)
 */
export function getDataRowCount(data: unknown): number {
  if (Array.isArray(data)) {
    return data.length;
  }
  if (typeof data === 'object' && data !== null) {
    // For objects, check if they have a 'data' property that's an array
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.data)) {
      return obj.data.length;
    }
    // Count top-level properties as "rows"
    return Object.keys(obj).length;
  }
  return 0;
}

/**
 * Check if data is too large for AI analysis
 * Limits:
 * - Max 1000 rows for arrays
 * - Max 1MB (1,048,576 bytes) for any data
 */
export function isDataTooLargeForAI(data: unknown): { tooLarge: boolean; reason?: string } {
  if (!data) {
    return { tooLarge: false };
  }

  // Check row count for arrays
  const rowCount = getDataRowCount(data);
  if (rowCount > 1000) {
    return {
      tooLarge: true,
      reason: `Data has ${rowCount} rows. Maximum allowed: 1000 rows.`
    };
  }

  // Check size in bytes
  const sizeInBytes = calculateDataSize(data);
  const maxSizeBytes = 1 * 1024 * 1024; // 1MB
  if (sizeInBytes > maxSizeBytes) {
    const sizeInMB = (sizeInBytes / (1024 * 1024)).toFixed(2);
    return {
      tooLarge: true,
      reason: `Data size is ${sizeInMB}MB. Maximum allowed: 1MB.`
    };
  }

  return { tooLarge: false };
}
